import {
	DashboardChangeEvent,
	DashboardConfig,
	DashboardProps,
	WidgetProps,
	WidgetsPanelColumnLayout,
	WidgetsPanelLayout,
} from '@sisense/sdk-ui';
import { HttpClient } from '@sisense/sdk-rest-client';
import { getDataSourceByTitle } from '@/services/data-sources/data-sources-service';
import {
	convertDataSource,
	convertJaqlDataSourceForDto,
	Filter,
	FilterRelations,
	isDataSourceInfo,
	JaqlDataSourceForDto,
} from '@sisense/sdk-data';
import deepEq from 'fast-deep-equal';
import { nanoidHex } from '@/utils/id-utils';

// Exported types and enums (remain outside the class)
export type DashboardChangeResult = {
	changed: boolean;
	updatedDashboardProps: DashboardProps;
};

// Core layout utilities (pure functions)

/**
 * Extract widget IDs from layout structure
 */
export function extractWidgetIdsFromLayout(
	layout: WidgetsPanelColumnLayout,
): string[] {
	return layout.columns.flatMap((column) =>
		column.rows.flatMap((row) => row.cells.map((cell) => cell.widgetId)),
	);
}

/**
 * Remove cells that reference non-existent widgets, empty rows, and empty columns
 */
export function cleanupOrphanedCells(
	layout: WidgetsPanelColumnLayout,
	existingWidgetIds: Set<string>,
): WidgetsPanelColumnLayout {
	const cleanedColumns = layout.columns
		.map((column) => ({
			...column,
			rows: column.rows
				.map((row) => ({
					...row,
					cells: row.cells.filter((cell) =>
						existingWidgetIds.has(cell.widgetId),
					),
				}))
				.filter((row) => row.cells.length > 0), // Remove empty rows
		}))
		.filter((column) => column.rows.length > 0); // Remove empty columns

	return cleanedColumns.length > 0
		? { columns: cleanedColumns }
		: { columns: [] };
}

/**
 * Set width to 100% if layout has only one column
 */
export function adjustSingleColumnWidth(
	layout: WidgetsPanelColumnLayout,
): WidgetsPanelColumnLayout {
	if (layout.columns.length === 1) {
		return {
			...layout,
			columns: [
				{
					...layout.columns[0],
					widthPercentage: 100,
				},
			],
		};
	}
	return layout;
}

/**
 * Find widgets that exist in dashboard but not in layout
 */
export function findOutOfPlaceWidgets(
	widgets: WidgetProps[],
	layout: WidgetsPanelColumnLayout | undefined,
): string[] {
	if (!layout) {
		return [];
	}

	const widgetsInLayout = extractWidgetIdsFromLayout(layout);
	return widgets
		.filter((widget) => !widgetsInLayout.includes(widget.id))
		.map((widget) => widget.id);
}

/**
 * Orchestrate all cleanup steps after any dashboard change
 */
export function cleanupDashboardAfterChange(
	dashboardProps: DashboardProps,
): DashboardChangeResult {
	let updatedProps = { ...dashboardProps };

	// Find and remove out-of-place widgets
	const outOfPlaceWidgetIds = findOutOfPlaceWidgets(
		updatedProps.widgets,
		updatedProps.layoutOptions?.widgetsPanel,
	);
	if (outOfPlaceWidgetIds.length > 0) {
		updatedProps = deleteWidgetsFromDashboard(
			updatedProps,
			outOfPlaceWidgetIds,
		);
	}

	// Clean up orphaned cells in layout
	if (updatedProps.layoutOptions?.widgetsPanel) {
		const existingWidgetIds = new Set(
			updatedProps.widgets.map((widget) => widget.id),
		);
		let cleanedLayout = cleanupOrphanedCells(
			updatedProps.layoutOptions.widgetsPanel,
			existingWidgetIds,
		);
		cleanedLayout = adjustSingleColumnWidth(cleanedLayout);

		updatedProps = {
			...updatedProps,
			layoutOptions: {
				...updatedProps.layoutOptions,
				widgetsPanel: cleanedLayout,
			},
		};
	}

	return { changed: true, updatedDashboardProps: updatedProps };
}

/**
 * Handle filters update
 */
function handleFiltersUpdate(
	dashboardProps: DashboardProps,
	filters: Filter[] | FilterRelations,
): DashboardChangeResult {
	return {
		changed: true,
		updatedDashboardProps: {
			...dashboardProps,
			filters,
		},
	};
}

/**
 * Handle filters panel collapse state change
 */
function handleFiltersPanelCollapse(
	dashboardProps: DashboardProps,
	collapsed: boolean,
): DashboardChangeResult {
	return {
		changed: true,
		updatedDashboardProps: {
			...dashboardProps,
			config: {
				...dashboardProps.config,
				filtersPanel: {
					...dashboardProps.config?.filtersPanel,
					collapsedInitially: collapsed,
				},
			},
		},
	};
}

/**
 * Handle widgets panel layout update
 */
function handleWidgetsPanelLayoutUpdate(
	dashboardProps: DashboardProps,
	layout: WidgetsPanelLayout,
): DashboardChangeResult {
	// somehow CSDK triggers the layout action after the layout is applied
	// and causing infinite re-renders. This is a quick fix to avoid that.
	if (deepEq(layout, dashboardProps.layoutOptions?.widgetsPanel)) {
		return { changed: false, updatedDashboardProps: dashboardProps };
	}

	return {
		changed: true,
		updatedDashboardProps: {
			...dashboardProps,
			layoutOptions: {
				...dashboardProps.layoutOptions,
				widgetsPanel: layout,
			},
		},
	};
}

/**
 * Handle widgets deletion
 */
function handleWidgetsDelete(
	dashboardProps: DashboardProps,
	widgetIds: string[],
): DashboardChangeResult {
	return {
		changed: true,
		updatedDashboardProps: deleteWidgetsFromDashboard(
			dashboardProps,
			widgetIds,
		),
	};
}

/**
 * Handle widgets panel layout isEditing change
 */
function handleWidgetsPanelLayoutIsEditingChange(
	dashboardProps: DashboardProps,
	isEditing: boolean,
): DashboardChangeResult {
	return {
		changed: true,
		updatedDashboardProps: {
			...dashboardProps,
			config: {
				...dashboardProps.config,
				widgetsPanel: {
					...dashboardProps.config?.widgetsPanel,
					editMode: {
						...dashboardProps.config?.widgetsPanel?.editMode,
						enabled: true,
						isEditing,
					},
				},
			},
		},
	};
}

export function getDashboardDataSourcesMap(
	dashboardProps: DashboardProps,
): Map<string, JaqlDataSourceForDto> {
	const dataSourcesMap = new Map<string, JaqlDataSourceForDto>();
	const { widgets, defaultDataSource } = dashboardProps;

	if (defaultDataSource && isDataSourceInfo(defaultDataSource)) {
		dataSourcesMap.set(
			defaultDataSource.title,
			convertJaqlDataSourceForDto(defaultDataSource),
		);
	}
	widgets.forEach((widget) => {
		if (
			'dataSource' in widget &&
			widget.dataSource &&
			isDataSourceInfo(widget.dataSource)
		) {
			const dataSourceTitle = widget.dataSource.title;
			if (!dataSourcesMap.has(dataSourceTitle)) {
				dataSourcesMap.set(
					dataSourceTitle,
					convertJaqlDataSourceForDto(widget.dataSource),
				);
			}
		}
	});

	return dataSourcesMap;
}

export function layoutDashboardWidgets(
	widgetOids: string[],
): WidgetsPanelColumnLayout {
	const widgetsLayout: WidgetsPanelColumnLayout =
		widgetOids.length === 1
			? {
					// use a 1-column layout
					columns: [
						{
							widthPercentage: 100,
							rows: widgetOids.map((widgetOid) => ({
								cells: [
									{
										widgetId: widgetOid,
										height: 384,
										widthPercentage: 100,
									},
								],
							})),
						},
					],
			  }
			: {
					// use a 2-column layout
					columns: [
						{
							widthPercentage: 50,
							rows: widgetOids
								.filter((_, index) => index % 2 === 0)
								.map((widgetOid) => ({
									cells: [
										{
											widgetId: widgetOid,
											height: 384,
											widthPercentage: 100,
										},
									],
								})),
						},
						{
							widthPercentage: 50,
							rows: widgetOids
								.filter((_, index) => index % 2 !== 0)
								.map((widgetOid) => ({
									cells: [
										{
											widgetId: widgetOid,
											height: 384,
											widthPercentage: 100,
										},
									],
								})),
						},
					],
			  };
	return widgetsLayout;
}

/**
 * Adds a widget to a new row in the column with the smallest number of rows and returns the updated layout.
 *
 * @param layout - The dashboard layout object to modify.
 * @param widgetId - The ID of the widget to add to the new row.
 * @returns The updated layout object with the new widget added.
 */
export function withWidgetAdded(
	layout: WidgetsPanelColumnLayout,
	widgetId: string,
): WidgetsPanelColumnLayout {
	// Create a new cell with the widgetId
	const newCell = {
		widthPercentage: 100, // Assuming the new cell takes the full width of the row
		height: '400px',
		widgetId: widgetId,
	};

	// Create a new row and add the new cell to it
	const newRow = {
		cells: [newCell],
	};

	// If there are no columns, create the first column
	if (!layout.columns || layout.columns.length === 0) {
		return {
			...(layout || { columns: [] }),
			columns: [
				{
					widthPercentage: 100,
					rows: [newRow],
				},
			],
		};
	}

	// Find the index of the column with the smallest number of rows
	let minRows = layout.columns[0].rows.length;
	let minIndex = 0;
	for (let i = 1; i < layout.columns.length; i++) {
		if (layout.columns[i].rows.length < minRows) {
			minRows = layout.columns[i].rows.length;
			minIndex = i;
		}
	}

	// Clone the layout to avoid mutating the original object
	return {
		...(layout || { columns: [] }),
		columns: layout.columns.map((column, index) =>
			index === minIndex
				? { ...column, rows: [...column.rows, newRow] }
				: column,
		),
	};
}

export function getBaseDashboardConfig(): DashboardConfig {
	return {
		toolbar: { visible: true },
		widgetsPanel: {
			responsive: true,
			editMode: {
				enabled: true,
				isEditing: true,
				applyChangesAsBatch: {
					enabled: false,
				},
			},
		},
		filtersPanel: {
			visible: true,
			collapsedInitially: false,
			actions: {
				addFilter: {
					enabled: true,
				},
				editFilter: {
					enabled: true,
				},
				deleteFilter: {
					enabled: true,
				},
			},
		},
	};
}

function getBaseDashboardProps(): DashboardProps {
	const dashboardProps: DashboardProps = {
		title: 'Dashboard Title',
		widgets: [],
		layoutOptions: {},
		config: getBaseDashboardConfig(),
		filters: [],
	};

	return dashboardProps;
}

export async function createEmptyDashboard(
	dataSourceTitle: string,
	dashboardTitle: string,
	httpClient?: HttpClient,
): Promise<DashboardProps> {
	const jaqlDataSource = await getDataSourceByTitle(
		dataSourceTitle,
		httpClient,
	);
	if (!jaqlDataSource) {
		throw new Error(
			'Error: Data source not found. Please ask the user to specify a valid data source.',
		);
	}
	const dataSource = convertDataSource(jaqlDataSource);

	return {
		...getBaseDashboardProps(),
		title: dashboardTitle,
		defaultDataSource: dataSource,
	};
}

export function resetWidgetForDashboard(widget: WidgetProps): WidgetProps {
	// regenerate Widget internal ID to make it unique for the dashboard
	return {
		...widget,
		id: nanoidHex(),
	};
}

export function addWidgetsToDashboard(
	dashboardProps: DashboardProps,
	widgetsToAdd: WidgetProps[],
): DashboardProps {
	return {
		...dashboardProps,
		widgets: [...dashboardProps.widgets, ...widgetsToAdd],
	};
}

export function updateDashboardWidgetsLayout(
	dashboardProps: DashboardProps,
	newWidgetIds: string[] = [],
): DashboardProps {
	const currentWidgetsLayout = dashboardProps.layoutOptions?.widgetsPanel;

	let widgetsLayout: WidgetsPanelColumnLayout = currentWidgetsLayout || {
		columns: [],
	};
	// for a dashboard without a layout, create a default layout
	// otherwise, update the layout with the new widget
	if (
		!currentWidgetsLayout ||
		!currentWidgetsLayout.columns ||
		currentWidgetsLayout.columns.length === 0
	) {
		widgetsLayout = layoutDashboardWidgets(
			dashboardProps.widgets.map((widget) => widget.id),
		);
	} else {
		// Ensure widgetsLayout is always defined
		if (!widgetsLayout) {
			widgetsLayout = layoutDashboardWidgets(
				dashboardProps.widgets.map((widget) => widget.id),
			);
		}
		newWidgetIds.forEach((widgetId) => {
			widgetsLayout = withWidgetAdded(widgetsLayout, widgetId);
		});
	}

	const updatedDashboardProps = {
		...dashboardProps,
		layoutOptions: { widgetsPanel: widgetsLayout },
	};

	return updatedDashboardProps;
}

export function updateDashboardTitle(
	dashboardProps: DashboardProps,
	dashboardTitle: string,
): DashboardProps {
	return {
		...dashboardProps,
		title: dashboardTitle,
	};
}

export async function createNewDashboard(
	dataSourceTitle: string,
	dashboardTitle: string,
	widgets: WidgetProps[] = [],
	httpClient?: HttpClient,
): Promise<DashboardProps> {
	const dashboardProps = await createEmptyDashboard(
		dataSourceTitle,
		dashboardTitle,
		httpClient,
	);
	return addWidgetsToExistingDashboard(dashboardProps, widgets);
}

export function addWidgetsToExistingDashboard(
	dashboardProps: DashboardProps,
	widgets: WidgetProps[],
): DashboardProps {
	if (widgets.length === 0) {
		return dashboardProps;
	}

	const updatedDashboardProps = addWidgetsToDashboard(
		dashboardProps,
		widgets,
	);

	const updatedDashboardPropsWithLayout = updateDashboardWidgetsLayout(
		updatedDashboardProps,
		widgets.map((widget) => widget.id),
	);

	return updatedDashboardPropsWithLayout;
}

export function deleteWidgetsFromDashboard(
	dashboardProps: DashboardProps,
	widgetIds: string[],
): DashboardProps {
	return {
		...dashboardProps,
		widgets: dashboardProps.widgets.filter(
			(widget) => !widgetIds.includes(widget.id),
		),
	};
}

// TODO: look into ways to do this in CSDK instead
function handleChange(
	dashboardProps: DashboardProps,
	event: DashboardChangeEvent,
): DashboardChangeResult {
	switch (event.type) {
		case 'filters/updated':
			return handleFiltersUpdate(dashboardProps, event.payload);
		case 'filtersPanel/collapse/changed':
			return handleFiltersPanelCollapse(dashboardProps, event.payload);
		case 'widgetsPanel/layout/updated':
			return handleWidgetsPanelLayoutUpdate(
				dashboardProps,
				event.payload,
			);
		case 'widgets/deleted':
			return handleWidgetsDelete(dashboardProps, event.payload);
		case 'widgetsPanel/editMode/isEditing/changed':
			return handleWidgetsPanelLayoutIsEditingChange(
				dashboardProps,
				event.payload,
			);
		default:
			return { changed: false, updatedDashboardProps: dashboardProps };
	}
}

export function changeDashboard(
	dashboardProps: DashboardProps,
	event: DashboardChangeEvent,
): DashboardChangeResult {
	// Delegate to specific handlers
	const { changed, updatedDashboardProps } = handleChange(
		dashboardProps,
		event,
	);

	// Apply cleanup if any changes were made
	if (changed) {
		return cleanupDashboardAfterChange(updatedDashboardProps);
	}

	return { changed, updatedDashboardProps };
}
